/**
 * /well-servicing/rigs/:id — one rig and its crew templates
 * (GET / PATCH / DELETE /well-servicing/rigs/:id, fieldiq-api#1269).
 *
 * Panels stack full width: the rig's details, then its crews, then the edit
 * form when editing (never a modal for the record itself).
 *
 * Edit and delete are WS_ADMIN (admin, office_staff); crews are WS_CREW_WRITE
 * (see RigCrews). Delete asks in-app first, and a rig that still has a crew is
 * refused by the API (409 rig_has_crews) with a sentence naming the crew —
 * shown in the dialog, never as a green toast.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Gauge, Pencil, Trash2, Users, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteWsRig, useUpdateWsRig, useWsEquipmentOptions, useWsMeta, useWsRig, useWsRigs } from '../../hooks/useWellServicing';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminWs,
  canWriteWsCrews,
  catalogLabel,
  duplicateRig,
  mastRatingText,
  rigRegistryLabel,
  wsRefusal,
} from '../../lib/well-servicing';
import { useBackToList } from '../downhole/recordPage';
import { RigCrews } from './RigCrews';
import { RigForm } from './RigForm';
import { isNotFound, WS_BAND_SECONDARY, WS_EYEBROW, WS_PRIMARY, WS_SECONDARY, WsDialog, WsEmpty, WsFormError, WsLoading, WsPanel, WsSpec } from './WsBits';

export default function WsRigDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-servicing/rigs');
  const { user } = useAuth();
  const query = useWsRig(id);
  const catalog = useWsMeta().data?.data ?? null;
  const canAdmin = canAdminWs(user?.role);
  const canCrews = canWriteWsCrews(user?.role);
  const [editing, setEditing] = useState(false);
  const rigs = useWsRigs();
  const equipment = useWsEquipmentOptions(canAdmin && editing);
  const update = useUpdateWsRig();
  const del = useDeleteWsRig();
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The duplicate link opens another rig on this same route: start it closed.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setDeleting(false);
  }, [id]);

  const rig = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const rigClass = rig ? rig.rigClassLabel ?? catalogLabel(catalog?.rigClasses, rig.rigClass) : '—';

  return (
    <div data-testid="ws-rig-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={rig ? rig.rigNumber || '—' : query.isLoading ? 'Rig' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          rig && canAdmin ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="fi-action fi-action--standard fi-action--primary"
                onClick={() => {
                  setEditError(null);
                  setDuplicateOf(null);
                  setEditing(true);
                }}
                disabled={editing}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Edit rig
              </button>
              <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: 'Class', tone: 'neutral', loading: query.isLoading, value: rigClass },
          { icon: <Gauge className="size-4" aria-hidden="true" />, label: 'Mast', tone: 'neutral', loading: query.isLoading, value: rig ? mastRatingText(rig) : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crews', tone: 'neutral', loading: query.isLoading, value: displayValue(rig?.crews?.length) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading rig" />
          ) : notFound || (!failure && !rig) ? (
            <WsPanel eyebrow="Rig" title="Rig">
              <WsEmpty>This rig was not found. It may have been deleted.</WsEmpty>
            </WsPanel>
          ) : failure || !rig ? (
            <WsPanel eyebrow="Rig" title="Rig">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rig" />
            </WsPanel>
          ) : (
            <>
              <WsPanel eyebrow="Rig" title="Details" testId="ws-rig-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WsSpec label="Rig number" value={rig.rigNumber || '—'} mono />
                  <WsSpec label={rigRegistryLabel(meta)} value={rig.caoecRigNumber || '—'} mono />
                  <WsSpec
                    label="Equipment unit"
                    value={
                      <Link to={`/equipment/${rig.equipmentId}`} className="underline">
                        {rig.equipmentName || '—'}
                        {rig.unitNumber ? ` · ${rig.unitNumber}` : ''}
                      </Link>
                    }
                  />
                  <WsSpec label="Serial number" value={rig.serialNumber || '—'} mono />
                  <WsSpec label="Rig class" value={rigClass} />
                  <WsSpec label="Mast rating" value={mastRatingText(rig)} mono />
                  <WsSpec label="Unit status" value={rig.equipmentStatus || '—'} />
                </dl>
                {rig.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{rig.notes}</p>}
              </WsPanel>

              <RigCrews rig={rig} catalog={catalog} canWrite={canCrews} />

              {editing && canAdmin && (
                <WsPanel eyebrow="Edit" title="Edit rig" testId="ws-rig-edit">
                  {equipment.isLoading ? (
                    <WsLoading label="Loading equipment" />
                  ) : (
                    <RigForm
                      key={rig.id}
                      rig={rig}
                      catalog={catalog}
                      meta={meta}
                      equipment={equipment.data ?? []}
                      rigs={rigs.data?.data ?? []}
                      pending={update.isPending}
                      error={editError}
                      duplicateOf={duplicateOf}
                      submitLabel="Save rig"
                      onCancel={() => setEditing(false)}
                      onSubmit={(body) => {
                        setEditError(null);
                        setDuplicateOf(null);
                        update.mutate(
                          { id: rig.id, body },
                          {
                            onSuccess: () => {
                              toast.success('Rig saved');
                              setEditing(false);
                            },
                            onError: (err) => {
                              const dup = duplicateRig(err);
                              setEditError(dup ? dup.message : wsRefusal(err, 'The rig could not be saved.'));
                              setDuplicateOf(dup?.existingRigId ?? null);
                            },
                          },
                        );
                      }}
                    />
                  )}
                </WsPanel>
              )}
            </>
          )}
        </div>
      </section>

      {rig && canAdmin && (
        <WsDialog
          open={deleting}
          title="Delete rig"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WS_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep rig
              </button>
              <button
                type="button"
                className={WS_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(rig.id, {
                    onSuccess: () => {
                      toast.success('Rig deleted');
                      navigate('/well-servicing/rigs', { replace: true });
                    },
                    onError: (err) => setDeleteError(wsRefusal(err, 'The rig could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete rig'}
              </button>
            </>
          }
        >
          <WsFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {rig.rigNumber} from the register. Its unit stays in Equipment.
            {rig.crews.length > 0 ? ' A rig with crews cannot be deleted: delete its crews first.' : ''}
          </p>
        </WsDialog>
      )}
    </div>
  );
}
